import React from 'react';
import { Dropdown, Label, Icon, Message, Button } from 'semantic-ui-react';

/**
 * Admin Override Selection Component
 * Displays dropdown for selecting admin user and override controls
 */
const AdminOverrideSelector = ({
  admins,
  selectedAdmin,
  onAdminChange,
  isOverrideActive,
  onToggleOverride,
  isLoading = false,
  useRealData = false,
  disabled = false
}) => {
  const adminOptions = Object.entries(admins || {}).map(([id, admin]) => ({
    key: id,
    value: id,
    text: `${admin.name} (Admin)${admin.isRealUser ? ' [Real]' : ' [Mock]'}`,
    icon: 'shield'
  }));

  return (
    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
        <label style={{ fontWeight: 'bold', marginRight: '10px' }}>
          <Icon name="shield" />
          Admin Override
        </label>
        {useRealData && (
          <Label size="mini" color="green">
            <Icon name="database" />
            Real Data
          </Label>
        )}
        {isOverrideActive && (
          <Label size="mini" color="orange">
            <Icon name="warning sign" />
            Override Active
          </Label>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px', alignItems: 'end' }}>
        <div style={{ flex: 1 }}>
          <Dropdown
            placeholder={isLoading ? "Loading admins..." : "Select admin..."}
            fluid
            selection
            loading={isLoading}
            disabled={disabled || isLoading}
            value={selectedAdmin || ''}
            options={adminOptions}
            onChange={(e, { value }) => onAdminChange(value)}
            noResultsMessage={
              Object.keys(admins || {}).length === 0
                ? (useRealData ? "No real admins found." : "No admins available.")
                : "No matching admins found"
            }
          />
        </div>

        <Button
          toggle
          active={isOverrideActive}
          onClick={onToggleOverride}
          disabled={!selectedAdmin || disabled}
          color={isOverrideActive ? 'orange' : 'grey'}
          icon={isOverrideActive ? 'unlock' : 'lock'}
          content={isOverrideActive ? 'Override ON' : 'Override OFF'}
          size="small"
        />
      </div>

      {selectedAdmin && admins[selectedAdmin] && (
        <div style={{
          marginTop: '8px',
          padding: '8px',
          backgroundColor: isOverrideActive ? '#fff3cd' : (admins[selectedAdmin].isRealUser ? '#e8f5e8' : '#f8f9fa'),
          borderRadius: '4px',
          border: isOverrideActive ? '1px solid #ffeaa7' : (admins[selectedAdmin].isRealUser ? '1px solid #c3e6c3' : '1px solid #dee2e6'),
          fontSize: '0.85em'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            <strong>{admins[selectedAdmin].name}</strong>
            {admins[selectedAdmin].isRealUser && (
              <Label size="mini" color="green" style={{ marginLeft: '8px' }}>
                <Icon name="database" />
                Real User
              </Label>
            )}
            {isOverrideActive && (
              <Label size="mini" color="orange" style={{ marginLeft: '8px' }}>
                <Icon name="warning sign" />
                Testing Mode
              </Label>
            )}
          </div>
          <div>
            <strong>Role:</strong> {admins[selectedAdmin].role} |
            <strong> Status:</strong> {admins[selectedAdmin].availability || 'Active'}
          </div>
          {admins[selectedAdmin].isRealUser && (
            <div>
              <strong>User ID:</strong> {admins[selectedAdmin].userId} |
              <strong> Profile:</strong> {admins[selectedAdmin].profileType || 'N/A'}
            </div>
          )}
          {isOverrideActive && (
            <div style={{ marginTop: '4px', color: '#856404' }}>
              <Icon name="info circle" />
              <strong>Override Mode:</strong> Admin can test any scenario combination and impersonate other user types
            </div>
          )}
        </div>
      )}

      {Object.keys(admins || {}).length === 0 && !isLoading && (
        <Message warning size="small" style={{ marginTop: '8px' }}>
          <Message.Header>No Admins Available</Message.Header>
          <p>
            {useRealData
              ? "No real admins could be loaded. Try switching to Mock Data mode."
              : "No mock admins are available."
            }
          </p>
        </Message>
      )}

      {!isOverrideActive && selectedAdmin && (
        <Message info size="small" style={{ marginTop: '8px' }}>
          <Message.Header>Admin Override Available</Message.Header>
          <p>
            Click "Override ON" to enable admin testing mode. This allows testing any scenario combination
            and bypassing normal role restrictions.
          </p>
        </Message>
      )}

      <div style={{ marginTop: '8px', fontSize: '0.8em', color: '#666' }}>
        <Icon name="info circle" />
        {Object.keys(admins || {}).length} admin{Object.keys(admins || {}).length !== 1 ? 's' : ''} available
        {isOverrideActive && ' • Override mode enables cross-role testing'}
      </div>
    </div>
  );
};

export default AdminOverrideSelector;
